import React, { useEffect, useState } from 'react';
import addLabel from 'helpers/addLabel';
import { useSelector, useDispatch } from 'react-redux';
import { ITopicFilterState } from './shared';
import { isArray } from 'lodash';

import { ReactComponent as IconClearFilter } from 'icons/icon-clear-filter.svg';
import sortByDisplayName from 'helpers/sortByDisplayName';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import { Button, ButtonVariant } from './TopicFilters.styles';
import RegionDropdown from 'components/base/TopicFiltersDropdown/RegionDropdown';
import Box from 'components/elements/Box';

import { ReactComponent as IconInclude } from 'icons/icon-include.svg';
import CustomRegionView from 'components/base/TopicFiltersDropdown/RegionDropdown/CustomRegionView';
import { RootState } from 'redux/rootReducer';
import {
  loadCustomRegions,
  removeCustomRegion,
  saveCustomRegion,
  shouldLoadCustomRegions,
} from 'redux/reducers/customRegions';
import DeleteConfirmationModal from 'components/base/TopicFiltersDropdown/RegionDropdown/DeleteConfirmationModal';
import { IRegionProps } from 'types/shared';
import { IRegionWithLabel } from 'components/base/TopicFiltersDropdown/shared/shared';
import { CUSTOM_USER_REGION_TYPE } from 'config';

interface IRegionFilterContainerProps {
  setFilter: any;
  clearFilterUrl: any;
  filters: ITopicFilterState;
  compareMode: any;
  isSmallScreen: boolean;
  regionsFromState: any;
  disabled?: boolean;
}

const DISABLE_MESSAGE =
  'If you’d like to select a region, please close compare.';

const RegionFilterContainer: React.FC<IRegionFilterContainerProps> = (
  props
) => {
  const {
    setFilter,
    clearFilterUrl,
    filters,
    compareMode,
    isSmallScreen,
    regionsFromState,
    disabled,
  } = props;

  const dispatch = useDispatch();

  const allCountries = useSelector((state: RootState) => state.countries.data)
    .map(addLabel)
    .sort(sortByDisplayName);

  const regionsFromFilters =
    typeof filters?.region === 'string' ? [filters?.region] : filters?.region;

  const regions: IRegionWithLabel[] = regionsFromState
    .map(addLabel)
    .sort(sortByDisplayName);

  const selectedFilter = regions.filter(
    (region: any): boolean => !!regionsFromFilters?.includes(region.id)
  );

  const [isAddCustomRegionView, setIsAddCustomRegionView] =
    useState<boolean>(false);

  const [isBackState, setIsBackState] = useState<boolean>(false);

  const onCreateRegion = (region: any, regionId?: string): void => {
    dispatch(saveCustomRegion(region, regionId));
    setIsAddCustomRegionView(false);
    setIsBackState(true);
    setEditCustomRegionId(undefined);
  };

  const customRegionsFromStore = useSelector(
    (state: RootState) => state.customRegions.data
  );

  useEffect(() => {
    if (dispatch(shouldLoadCustomRegions())) {
      dispatch(loadCustomRegions());
    }
  }, [dispatch]);

  const customRegionsWithLabels: IRegionWithLabel[] = Object.values(
    customRegionsFromStore
  )
    .map((region: any) => ({
      ...addLabel(region),
      regionType: CUSTOM_USER_REGION_TYPE,
    }))
    .sort(sortByDisplayName);

  const customRegionsFromFilters =
    typeof filters?.customRegion === 'string'
      ? [filters?.customRegion]
      : filters?.customRegion;

  const selectedCustomFilter = customRegionsWithLabels.filter(
    (cRegion: any): boolean => !!customRegionsFromFilters?.includes(cRegion.id)
  );

  const onChange = (item: IRegionProps): void => {
    if (item?.regionType === CUSTOM_USER_REGION_TYPE) {
      const isChecked = customRegionsFromFilters?.includes(item?.id);

      let newCustomRegionsList = [item?.id];
      if (isChecked && customRegionsFromFilters?.length) {
        // remove from current custom regions
        newCustomRegionsList = customRegionsFromFilters?.filter(
          (region: any) => region !== item?.id
        );
      } else if (customRegionsFromFilters?.length) {
        // add to current custom regions
        newCustomRegionsList = [...customRegionsFromFilters, item?.id];
      }

      if (newCustomRegionsList.length) {
        setFilter('customRegion', newCustomRegionsList);
        logToAmplitude('Filter has been updated', {
          type: 'Region View',
          value: newCustomRegionsList,
        });
      } else {
        clearFilterUrl('customRegion');
        logToAmplitude('Filter has been updated', { type: 'Region View' });
      }
    } else {
      const isChecked = regionsFromFilters?.includes(item?.id);

      let newRegionsList = [item?.id];
      if (isChecked && regionsFromFilters?.length) {
        // remove from current regions
        newRegionsList = regionsFromFilters?.filter(
          (region: any) => region !== item?.id
        );
      } else if (regionsFromFilters?.length) {
        // add to current regions
        newRegionsList = [...regionsFromFilters, item?.id];
      }

      if (newRegionsList.length) {
        setFilter('region', newRegionsList);
        logToAmplitude('Filter has been updated', {
          type: 'Region View',
          value: newRegionsList,
        });
      } else {
        clearFilterUrl('region');
        logToAmplitude('Filter has been updated', { type: 'Region View' });
      }
    }
  };

  const [editCustomRegionId, setEditCustomRegionId] = useState<
    string | undefined
  >(undefined);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const deleteAction = (): void => {
    if (editCustomRegionId) {
      // Clear this filter in URL (if this custom region is selected)
      if (
        customRegionsFromFilters &&
        customRegionsFromFilters.includes(editCustomRegionId)
      ) {
        const filteredCustomRegions = customRegionsFromFilters.filter(
          (region) => region !== editCustomRegionId
        );
        setFilter('customRegion', filteredCustomRegions);
        logToAmplitude('Filter has been updated', {
          type: 'Region View',
          value: filteredCustomRegions,
        });
      }

      setEditCustomRegionId(undefined);

      dispatch(removeCustomRegion(editCustomRegionId));
    }
  };

  const clearRegionFilter = (): void => {
    if (regionsFromFilters || customRegionsFromFilters) {
      clearFilterUrl(['customRegion', 'region']);
      logToAmplitude('Filter has been updated', { type: 'Region View' });
    }
  };

  return (
    <>
      {!isAddCustomRegionView && (
        <RegionDropdown
          onClose={(): void => setIsBackState(false)}
          customRegions={customRegionsWithLabels}
          onChange={onChange}
          items={regions}
          selectedItem={selectedCustomFilter[0] || selectedFilter[0] || null}
          selectedItems={[...selectedCustomFilter, ...selectedFilter]}
          defaultLabel="Select"
          disabled={disabled || compareMode === 'country'}
          disabledMessage={
            compareMode === 'country' ? DISABLE_MESSAGE : undefined
          }
          isSmallScreen={isSmallScreen}
          clearFilter={clearRegionFilter}
          isOpened={isBackState}
          switchToEditView={(customRegionId: string): void => {
            setIsAddCustomRegionView(true);
            setEditCustomRegionId(customRegionId);
          }}
        >
          <div>
            <Box p={3}>
              <ButtonVariant
                margin="6px 0"
                onClick={(): void => {
                  setIsAddCustomRegionView(true);
                  setEditCustomRegionId(undefined);
                }}
              >
                <span>
                  <IconInclude color="#001247" />
                  <span>ADD CUSTOM REGION</span>
                </span>
              </ButtonVariant>
            </Box>

            <Button
              borderColor={'LightGrey'}
              borderWidth={'1px'}
              borderStyle={'solid'}
              fontSize={14}
              py={4}
              isActive={!!selectedFilter}
              onClick={clearRegionFilter}
              isClearButton
            >
              {selectedFilter && <IconClearFilter />}
              Reset to Global
            </Button>
          </div>
        </RegionDropdown>
      )}

      {/* Add/Edit Custom Region view */}
      {isAddCustomRegionView && (
        <CustomRegionView
          onClose={(): void => {
            setIsBackState(false);
            setIsAddCustomRegionView(false);
          }}
          countryItems={allCountries}
          regionNames={[...regions, ...customRegionsWithLabels].map(
            (r: any) => r.displayName
          )}
          defaultLabel="Select"
          isOpened={true}
          disabled={compareMode === 'country'}
          disabledMessage={
            compareMode === 'country' ? DISABLE_MESSAGE : undefined
          }
          itemToString={(item: any): string => item?.id}
          isSmallScreen={isSmallScreen}
          clearFilter={clearRegionFilter}
          returnBack={(): void => {
            setIsAddCustomRegionView(false);
            setIsBackState(true);
          }}
          onCreateRegion={onCreateRegion}
          editCustomRegion={
            editCustomRegionId &&
            customRegionsFromStore &&
            !isArray(customRegionsFromStore)
              ? customRegionsFromStore[editCustomRegionId]
              : undefined
          }
          onDelete={(): void => setShowDeleteModal(true)}
          selectedItem={selectedCustomFilter[0] || null}
          selectedItems={[...selectedCustomFilter, ...selectedFilter]}
        />
      )}

      {showDeleteModal && editCustomRegionId && (
        <DeleteConfirmationModal
          deleteFunction={deleteAction}
          hideModal={(): void => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};

export default RegionFilterContainer;
